/**
 * User Billing History Page
 * Shows user's billing history and spending by ability
 */

import { useEffect, useState } from 'react'
import { CreditCard, TrendingUp, Calendar, Filter, ChevronDown, ChevronUp } from 'lucide-react'
import api from '@/services/api'

interface ChargeItem {
  id: string
  ability_name: string
  ability_display_name: string
  gross_amount: number
  description: string | null
  created_at: string
}

interface AbilitySummary {
  ability_name: string
  ability_display_name: string
  transaction_count: number
  total_amount: number
}

interface BillingSummary {
  period: string
  start_date: string
  end_date: string
  total_spent: number
  transaction_count: number
  by_ability: AbilitySummary[]
}

export default function BillingHistory() {
  const [summary, setSummary] = useState<BillingSummary | null>(null)
  const [charges, setCharges] = useState<ChargeItem[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<string>('month')
  const [selectedAbility, setSelectedAbility] = useState<string>('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [expandedAbility, setExpandedAbility] = useState<string | null>(null)

  useEffect(() => {
    loadSummary()
  }, [period])

  useEffect(() => {
    loadCharges()
  }, [page, selectedAbility])

  const loadSummary = async () => {
    try {
      const response = await api.get('/billing/summary', {
        params: { period },
      })
      setSummary(response.data)
    } catch (error) {
      console.error('Failed to load billing summary:', error)
    }
  }

  const loadCharges = async () => {
    setLoading(true)
    try {
      const response = await api.get('/billing/history', {
        params: {
          page,
          page_size: 20,
          ability: selectedAbility || undefined,
        },
      })
      setCharges(response.data.charges)
      setTotal(response.data.total)
    } catch (error) {
      console.error('Failed to load charges:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (amount: number) => {
    return `KES ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const totalPages = Math.ceil(total / 20)

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-purple-400" />
              Billing History
            </h1>
            <p className="text-gray-400 mt-1">Track your spending on Niki services</p>
          </div>

          {/* Period Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-xl p-6 border border-purple-500/30">
              <p className="text-sm text-purple-300">Total Spent</p>
              <p className="text-3xl font-bold text-white mt-1">
                {formatCurrency(summary.total_spent)}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {summary.start_date} - {summary.end_date}
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <p className="text-sm text-gray-400">Transactions</p>
              <p className="text-3xl font-bold text-white mt-1">
                {summary.transaction_count}
              </p>
              <p className="text-xs text-gray-400 mt-2">Total operations</p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <p className="text-sm text-gray-400">Services Used</p>
              <p className="text-3xl font-bold text-white mt-1">
                {summary.by_ability.length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Different abilities</p>
            </div>
          </div>
        )}

        {/* Spending by Ability */}
        {summary && summary.by_ability.length > 0 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 mb-8">
            <div className="px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                Spending by Service
              </h2>
            </div>
            <div className="divide-y divide-slate-700">
              {summary.by_ability.map((ability) => (
                <button
                  key={ability.ability_name}
                  onClick={() => {
                    setExpandedAbility(
                      expandedAbility === ability.ability_name ? null : ability.ability_name
                    )
                    setSelectedAbility(
                      selectedAbility === ability.ability_name ? '' : ability.ability_name
                    )
                    setPage(1)
                  }}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium">{ability.ability_display_name}</p>
                      <p className="text-sm text-gray-400">{ability.transaction_count} transactions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-white font-medium">{formatCurrency(ability.total_amount)}</p>
                      <p className="text-xs text-gray-500">
                        {((ability.total_amount / summary.total_spent) * 100).toFixed(1)}% of total
                      </p>
                    </div>
                    {expandedAbility === ability.ability_name ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Transaction History */}
        <div className="bg-slate-800 rounded-xl border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Transaction History</h2>
            {selectedAbility && (
              <button
                onClick={() => {
                  setSelectedAbility('')
                  setExpandedAbility(null)
                  setPage(1)
                }}
                className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                <Filter className="w-4 h-4" />
                Clear filter
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto" />
              <p className="text-gray-400 mt-4">Loading transactions...</p>
            </div>
          ) : charges.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions found</p>
              {selectedAbility && (
                <p className="text-sm mt-2">Try clearing the filter to see all transactions</p>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-700">
                {charges.map((charge) => (
                  <div key={charge.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{charge.ability_display_name}</p>
                        <p className="text-sm text-gray-400">
                          {charge.description || 'No description'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(charge.created_at)}</p>
                      </div>
                    </div>
                    <p className="text-white font-medium">{formatCurrency(charge.gross_amount)}</p>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <p className="text-sm text-gray-400">
                    Showing {(page - 1) * 20 + 1} - {Math.min(page * 20, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 rounded bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1 rounded bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
